import { Dispatch, SetStateAction } from "react";

export const enum CourseStatuses {
  NotStarted = "notstarted",
  InProgress = "inprogress",
  Passed = "passed",
  New = "new",
  Required = "required",
  Unknown = "",
}

export type CourseFromList = {
  chapterId: string,
  chapterTitle: string,
  completedLessonsCount?: number,
  courseId: string,
  courseTitle: string,
  img: string,
  lessonsCount: number,
  statuses: string[],
  unitId: string,
};

export type Course = {
  available: boolean,
  completedLessons: number,
  dateStart: string,
  description: string,
  elements: Lesson[],
  image: string,
  lessonsCount: number,
  mainChapterTitle: string,
  pubSurvey: string,
  requirement: string,
  status: string,
  teacher: string,
  teacherValue: string,
  time: string,
  title: string,
  uid: string,
  relativeUnit: string,
  hasSuccessScreen: boolean,
  feedback: boolean,
  surveyShowAlways: boolean,
  pubSurveyPassed: boolean,
};

export type Lesson = {
  alwaysAvailable: string,
  attemptsCount: string,
  attemptsNumber: string,
  banDownload: string,
  blockReason: string,
  blocked: string,
  buttonText: string,
  daysToOpen: string,
  description: string,
  elementSource: string,
  file: string,
  hideCard: boolean,
  hideResults: string,
  ignoreResults: string,
  image: string,
  limit: string,
  link: string,
  materialSurvey: string,
  materialType: LessonType,
  materialTypeVal: string,
  passRate: string,
  payload: string,
  preview: string,
  requirement: LessonRequirement,
  richcontent: string,
  richcontentJson: string,
  richcontentHtml: string,
  showReport: string,
  starsCount: string,
  status: LessonStatus,
  survey: string,
  surveyComment1: string,
  surveyComment2: string,
  teacher: string,
  teacherValue: string,
  time: string,
  title: string,
  uid: string,
  unitid: string,
  visibility: string,
  visibilityCondition: string,
  richcontentAbsolute: string,
  notALesson: boolean,
  isFinished: boolean,
};

export type ExtendedLesson = {
  nodeId: string,
  unitDescription?: string,
  isLastLessonInUnit?: boolean,
  parentNodeIds?: string[] | null,
  unitParentId?: string,
  isParentAvailable?: boolean,
  isReadyToComplete?: boolean,
} & Lesson;

export const enum LessonRequirement {
  Required = "necessary",
  NotRequired = "unnecessary",
}

export enum LessonType {
  Video = "tpl_lms_material_type_video",
  Scorm = "tpl_lms_material_type_scorm",
  Test = "tpl_lms_material_type_test",
  Pdf = "tpl_lms_material_type_pdf",
  Presentation = "tpl_lms_material_type_pptx",
  Photo = "tpl_lms_material_type_jpeg",
  Photo2 = "tpl_lms_material_type_image",
  Longread = "tpl_lms_material_type_longread",
  Longread2 = "tpl_lms_material_type_longread2",
  Longread3 = "tpl_lms_material_type_longread3",
  Audio = "tpl_lms_material_type_audio",
  Folder = "tpl_lms_material_type_folder",
}

export const enum LessonStatus {
  NotStarted = "notstarted",
  InProgress = "inprogress",
  Passed = "passed",
  Failed = "failed",
  Queued = "queued",
}

export const enum BackTestQuestionType {
  Open = "open",
  Closed = "closed",
  Sequence = "sequence",
  Survey = "survey",
  Matching = "matching",
}

export const enum TestQuestionFrontType {
  Open = "open",
  ClosedOne = "closed-one",
  ClosedMany = "closed-many",
  Sequence = "sequence",
  Survey = "survey",
  Matching = "matching",
}

export type TestQuestionData = {
  answer: string,
  answers: string,
  currentAnswers: string,
  fileAnswer: string,
  leftAnswers: string,
  rightAnswers: string,
  multiselect: string,
  question: string,
  questionType: BackTestQuestionType,
  questionTypeValue: string,
  shuffleButtons: string,
  status: string,
  uid: string,
  userAnswer: string,
  ball?: string | null,
  maxball?: string | null,
  uploadFile?: string,
  questionImage?: string,
  validation?: string,
  validationInfo?: string,
  surveyComment1: string,
  surveyComment2: string,
  stars: string,
  starsCount: string,
  requiredFeedback: string,
  additionalQuestionTitle: string,
  necessaryUserComment: string,
  userComment: string,
  triggerVariants: string,
  userCommentPlaceholder: string,
  descriptionLongread: string,
};

export const enum TestSolutionStatus {
  UnderReview = "2024-05-27T13-49-53z03-00-2h2zWh",
  Success = "2024-06-17T14-44-44z03-00-2i0Pn2",
  Fail = "2024-06-17T14-46-32z03-00-2i0Q0d",
  InProgress = "2024-05-20T16-54-14z03-00-2gja5B",
  Stopped = "2024-05-29T15-30-34z03-00-2h8q1D",
}

export type TestData = {
  checkType: string,
  description: string,
  questions: {
    answer: string,
    answers: string,
    leftAnswers: string,
    multiselect: string,
    question: string,
    questionType: BackTestQuestionType,
    questionTypeValue: string,
    rightAnswers: string,
    shuffleButtons: string,
    status: string,
    uid: string,
    userAnswer: string,
  }[],
  session: string,
  skillLevel: string,
  status: TestSolutionStatus,
  statusValue: string,
  survey: string,
  remainingTime: number,
  testTimeout: string,
  testmap: string,
  title: string,
  uid: string,
};

export type ValidationError = {
  hasError: boolean,
  message: string,
};

export type QuestionComponentProps = {
  answers: string[],
  userAnswer: unknown,
  setUserAnswer: Dispatch<SetStateAction<unknown>>,
  isFileUpload: boolean,
  setFileAnswer: Dispatch<SetStateAction<string>>,
  fileAnswer: string,
  error: ValidationError,
  setError: Dispatch<SetStateAction<ValidationError>>,
  currentQuestion: TestQuestionData,
};

export type FinishTestResult = {
  expired: boolean,
};

export type SaveUnitResult = {
  hasSuccessScreen: boolean,
};

export type GetScormProgressResponse = {
  payload: Record<string, unknown>,
  type: string,
  currentPercent: number,
  passPercent: number,
  error?: string,
};

export type SetScormProgressRequest = {
  scorm: string,
  payload: Record<string, unknown>,
  type: string,
  progressMeasure: string,
  publicationid: string,
  unitid: string,
};

export type SetScormProgressResponse = {
  currentPercent?: number,
  passPercent?: number,
  type: string,
  error?: string,
};

export enum BorderRadiusSize {
  S = "s",
  M = "m",
  B = "b",
}

export type TestReportQuestion = {
  uid: string,
  question: string,
  userAnswer: string,
  correctAnswer: string,
  ball: number,
};

export type TestReportRequest = {
  error: string,
  questions: TestReportQuestion[],
};
